import React, { useMemo, useCallback } from 'react';
import { useTranslation } from 'localization';
import {
  MarketplaceDocument,
  MarketplaceChannelDocument,
  MarketplaceItemDocument,
  MarketplacePublisherDocument,
} from 'types/marketplace';
import type { PublisherDetail, PublisherStatus } from 'types/publisher';
// `SearchFacetsMolecule` left rail removed — filtering now happens via the
// parent page's scope-chip + subscription-state filter row above the search
// area. Imports kept for the prop types (`ActiveFilters`, `SearchFacets`) so
// the public API stays backwards-compatible with existing callers.
import type {
  ActiveFilters,
  SearchFacets,
} from '../../molecules/marketplace/SearchFacetsMolecule';
import UseCaseTagPillsAtom from '../../atoms/marketplace/UseCaseTagPillsAtom';
import PublisherCardMolecule from '../../molecules/marketplace/PublisherCardMolecule';
import './SearchResultsOrganism.scss';

type SortOption = 'relevance' | 'rating' | 'subscribers' | 'recent';

export interface SearchResultsOrganismProps {
  results: MarketplaceDocument[];
  total: number;
  loading: boolean;
  error: string | null;
  hasMore: boolean;
  query: string;
  facets: SearchFacets;
  activeFilters: ActiveFilters;
  sortValue: SortOption;
  onFilterChange: (filters: ActiveFilters) => void;
  onSortChange: (sort: SortOption) => void;
  onLoadMore: () => void;
  onChannelClick: (channelId: string) => void;
  onItemClick?: (item: MarketplaceItemDocument) => void;
  onPublisherClick?: (slug: string) => void;
  onTagClick?: (tag: string) => void;
}

/** Build a card-compatible PublisherDetail from the slimmer index doc. */
function publisherDocToCard(doc: MarketplacePublisherDocument): PublisherDetail {
  return {
    id: doc.id,
    system_name: doc.publisher_system_name,
    slug: doc.publisher_slug,
    display_name: doc.display_name,
    display_name_i18n: { en: doc.display_name },
    bio: doc.bio,
    bio_i18n: { en: doc.bio },
    locales: ['en'],
    default_locale: 'en',
    website: null,
    socials: null,
    hue: doc.hue ?? 210,
    glyph: doc.display_name.charAt(0).toUpperCase(),
    glyph_kind: 'letter',
    is_official: doc.is_official,
    is_featured: doc.is_featured,
    is_archived: false,
    logo_attachment_id: null,
    banner_attachment_id: null,
    status: doc.status as PublisherStatus,
    reviewer_note: null,
    submitted_at: null,
    reviewed_at: null,
    published_at: null,
    reviewed_by: null,
    created_by: '',
    updated_by: '',
    created_at: new Date(doc.created_at),
    updated_at: new Date(doc.updated_at),
    channelCount: doc.channel_count,
    subscriberCount: 0,
    followerCount: doc.follower_count,
    isFollowed: false,
    permissions: {
      canEdit: false,
      canApprove: false,
      canSubmitForReview: false,
      canFollow: false,
      canDelete: false,
    } as any,
  };
}

const ITEM_TYPE_ICONS: Record<string, string> = {
  approach: '\uD83D\uDCCB',
  surface_template: '\uD83D\uDDBC\uFE0F',
  assistant: '\uD83E\uDD16',
  video: '\uD83C\uDFAC',
  form: '\uD83D\uDCDD',
};

const ITEM_TYPE_COLORS: Record<string, string> = {
  approach: '#2563eb',
  surface_template: '#7c3aed',
  assistant: '#059669',
  video: '#dc2626',
  form: '#d97706',
};

const SearchResultsOrganism: React.FC<SearchResultsOrganismProps> = ({
  results,
  total,
  loading,
  error,
  hasMore,
  query,
  // facets / activeFilters / sortValue / onFilterChange / onSortChange
  // intentionally unused — left in the interface for caller backwards
  // compatibility but no longer consumed (sidebar + inline sort were dropped
  // in the Path C redesign; filtering + sort live on the parent page).
  onLoadMore,
  onChannelClick,
  onItemClick,
  onPublisherClick,
  onTagClick,
}) => {
  const { t } = useTranslation();

  const { publishers, channels, items } = useMemo(() => {
    const pub: MarketplacePublisherDocument[] = [];
    const ch: MarketplaceChannelDocument[] = [];
    const it: MarketplaceItemDocument[] = [];
    for (const doc of results) {
      if (doc.doc_type === 'channel') {
        ch.push(doc);
      } else if (doc.doc_type === 'channel_item') {
        it.push(doc);
      } else if (doc.doc_type === 'publisher') {
        pub.push(doc);
      }
    }
    // Pin the Center Channel (the lone system channel) to the top of the
    // Channels group regardless of relevance/popularity ranking.
    ch.sort((a, b) => {
      const aSys = a.is_system ? 1 : 0;
      const bSys = b.is_system ? 1 : 0;
      return bSys - aSys;
    });
    return { publishers: pub, channels: ch, items: it };
  }, [results]);

  const handleChannelKeyDown = useCallback(
    (e: React.KeyboardEvent, channelId: string) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onChannelClick(channelId);
      }
    },
    [onChannelClick]
  );

  const handleItemKeyDown = useCallback(
    (e: React.KeyboardEvent, item: MarketplaceItemDocument) => {
      if (onItemClick && (e.key === 'Enter' || e.key === ' ')) {
        e.preventDefault();
        onItemClick(item);
      }
    },
    [onItemClick]
  );

  const renderSkeleton = () => (
    <div className="marketplace-search-results__skeleton">
      {[1, 2, 3, 4, 5, 6].map((i) => (
        <div key={i} className="marketplace-search-results__skeleton-card" />
      ))}
    </div>
  );

  const renderChannelCard = (doc: MarketplaceChannelDocument) => (
    <div
      key={`channel-${doc.id}`}
      className={`marketplace-search-results__card marketplace-search-results__card--channel marketplace-search-results__card--scope-${doc.scope}`}
      role="button"
      tabIndex={0}
      onClick={() => onChannelClick(doc.id)}
      onKeyDown={(e) => handleChannelKeyDown(e, doc.id)}
    >
      <div className="marketplace-search-results__card-header">
        <h4 className="marketplace-search-results__card-name">{doc.name}</h4>
        <span
          className={`marketplace-search-results__card-scope-chip marketplace-search-results__card-scope-chip--${doc.scope}`}
        >
          {t(`marketplace.common.scope.${doc.scope}`)}
        </span>
      </div>
      {doc.description && (
        <p className="marketplace-search-results__card-description">
          {doc.description}
        </p>
      )}
      <div className="marketplace-search-results__card-stats">
        {doc.subscriber_count > 0 && (
          <span className="marketplace-search-results__card-stat">
            <i
              className="pi pi-users marketplace-search-results__card-stat-icon"
              aria-hidden="true"
            />
            {t('marketplace.common.subscriberCount', {
              count: doc.subscriber_count,
            })}
          </span>
        )}
        {doc.item_count != null && doc.item_count > 0 && (
          <span className="marketplace-search-results__card-stat">
            <i
              className="pi pi-box marketplace-search-results__card-stat-icon"
              aria-hidden="true"
            />
            {t('marketplace.common.itemCount', { count: doc.item_count })}
          </span>
        )}
      </div>
      {doc.use_case_tags && doc.use_case_tags.length > 0 && (
        <UseCaseTagPillsAtom
          tags={doc.use_case_tags}
          maxVisible={3}
          size="small"
          onTagClick={onTagClick}
          className="marketplace-search-results__card-tags"
        />
      )}
    </div>
  );

  const renderItemCard = (doc: MarketplaceItemDocument) => {
    const icon = ITEM_TYPE_ICONS[doc.item_type] || '';
    return (
      <div
        key={`item-${doc.id}`}
        className={[
          'marketplace-search-results__card',
          'marketplace-search-results__card--item',
          onItemClick ? 'marketplace-search-results__card--clickable' : '',
        ]
          .filter(Boolean)
          .join(' ')}
        role={onItemClick ? 'button' : undefined}
        tabIndex={onItemClick ? 0 : undefined}
        onClick={onItemClick ? () => onItemClick(doc) : undefined}
        onKeyDown={onItemClick ? (e) => handleItemKeyDown(e, doc) : undefined}
      >
        {/* TYPE label at top of the item card — matches the new design's
            compact "APPROACH / ASSISTANT / VIDEO" affordance. */}
        {doc.item_type && (
          <span
            className={`marketplace-search-results__card-type-label marketplace-search-results__card-type-label--${doc.item_type}`}
            style={{ color: ITEM_TYPE_COLORS[doc.item_type] || undefined }}
          >
            {icon && (
              <span
                className="marketplace-search-results__card-type-icon"
                aria-hidden="true"
              >
                {icon}
              </span>
            )}
            {t(`marketplace.common.itemType.${doc.item_type}`)}
          </span>
        )}
        <h4 className="marketplace-search-results__card-name">{doc.item_name}</h4>
        {doc.item_description && (
          <p className="marketplace-search-results__card-description">
            {doc.item_description}
          </p>
        )}
        {doc.channel_name && (
          <div className="marketplace-search-results__card-footer">
            <i
              className="pi pi-th-large marketplace-search-results__card-footer-icon"
              aria-hidden="true"
            />
            <span className="marketplace-search-results__card-footer-prefix">
              {t('marketplace.search.inChannel')}
            </span>
            <button
              type="button"
              className="marketplace-search-results__card-channel-link"
              onClick={(e) => {
                e.stopPropagation();
                onChannelClick(doc.channel_id);
              }}
            >
              {doc.channel_name}
            </button>
          </div>
        )}
        {doc.use_case_tags && doc.use_case_tags.length > 0 && (
          <UseCaseTagPillsAtom
            tags={doc.use_case_tags}
            maxVisible={2}
            size="small"
            onTagClick={onTagClick}
            className="marketplace-search-results__card-tags"
          />
        )}
      </div>
    );
  };

  const renderContent = () => {
    if (loading && results.length === 0) {
      return renderSkeleton();
    }

    if (error) {
      return (
        <div className="marketplace-search-results__error">
          <p className="marketplace-search-results__error-message">
            {t('marketplace.common.errorSearchFailed')}
          </p>
        </div>
      );
    }

    if (results.length === 0 && !loading) {
      return (
        <div className="marketplace-search-results__empty">
          <span className="marketplace-search-results__empty-icon">&#128269;</span>
          <p>{t('marketplace.search.noResults', { query })}</p>
          <p className="marketplace-search-results__empty-hint">
            {t('marketplace.search.noResultsHint')}
          </p>
        </div>
      );
    }

    return (
      <>
        {publishers.length > 0 && (
          <section className="marketplace-search-results__section">
            <h3 className="marketplace-search-results__section-title">
              {t('marketplace.search.docTypes.publisher')}
              <span className="marketplace-search-results__section-count">
                {publishers.length}
              </span>
            </h3>
            <div className="marketplace-search-results__grid marketplace-search-results__grid--wide">
              {publishers.map((doc) => (
                <PublisherCardMolecule
                  key={`publisher-${doc.id}`}
                  publisher={publisherDocToCard(doc)}
                  onClick={onPublisherClick}
                />
              ))}
            </div>
          </section>
        )}

        {channels.length > 0 && (
          <section className="marketplace-search-results__section">
            <h3 className="marketplace-search-results__section-title">
              {t('marketplace.search.docTypes.channel')}
              <span className="marketplace-search-results__section-count">
                {channels.length}
              </span>
            </h3>
            <div className="marketplace-search-results__grid marketplace-search-results__grid--wide">
              {channels.map(renderChannelCard)}
            </div>
          </section>
        )}

        {items.length > 0 && (
          <section className="marketplace-search-results__section">
            <h3 className="marketplace-search-results__section-title">
              {t('marketplace.search.docTypes.channel_item')}
              <span className="marketplace-search-results__section-count">
                {items.length}
              </span>
            </h3>
            <div className="marketplace-search-results__grid">
              {items.map(renderItemCard)}
            </div>
          </section>
        )}

        {hasMore && (
          <div className="marketplace-search-results__load-more">
            <button
              type="button"
              className="marketplace-search-results__load-more-btn"
              onClick={onLoadMore}
              disabled={loading}
            >
              {loading
                ? t('marketplace.common.loading')
                : t('marketplace.common.loadMore')}
            </button>
          </div>
        )}
      </>
    );
  };

  return (
    <div className="marketplace-search-results">
      <div className="marketplace-search-results__main">
        <div className="marketplace-search-results__header">
          <h2 className="marketplace-search-results__title">
            {query
              ? t('marketplace.search.resultsForQuery', { query })
              : t('marketplace.search.allResults')}
          </h2>
          <span className="marketplace-search-results__count">
            {t('marketplace.search.resultCount', { count: total })}
          </span>
        </div>
        {renderContent()}
      </div>
    </div>
  );
};

export default SearchResultsOrganism;
